import type { NpcSnap, Role } from '../net/protocol';
import { COMBAT } from '../net/protocol';
import { NPC_TUNING, stageFromMood, saturationFactor } from './npc_aggro';
import type { AABB2 } from './arenas';

type ServerNpc = {
  id: number;
  x: number;
  z: number;
  yaw: number;
  home: { x: number; z: number };
  wanderX: number;
  wanderZ: number;
  wanderPause: number;
  mood: number;              // signed, unbounded
  prevStage: 0 | 1 | 2 | 3;
  respawnAtMs: number;       // >0 while hidden between lives
  shinyUntilMs: number;
};

export type AttackerInfo = {
  id: string;
  role: Role;
  x: number;
  z: number;
  hood: boolean;
  absorptionMult: number;    // 1.0 for starter weapons, items bump this up later
};

export type NpcXpGrant = { attackerId: string; amount: number };
export type NpcShoutEvent = { npcId: number; x: number; z: number; mood: number };

export class NpcManager {
  private npcs: ServerNpc[] = [];
  private nextId = 1;
  // CP6 — per-mode override. Defaults to NPC_TUNING.COUNT until the
  // server overrides via setTargetCount() at warmup→playing.
  private targetCount: number = NPC_TUNING.COUNT;
  // Per-arena spawn box. Defaults to the legacy Classic-80u box until
  // the server calls setSpawnBounds(arena.npcBounds) at warmup.
  private spawnBounds: AABB2 = NPC_TUNING.SPAWN_BOUNDS;

  constructor() {
    for (let i = 0; i < this.targetCount; i++) this.npcs.push(this.makeNpc());
  }

  private makeNpc(): ServerNpc {
    const x = randRange(this.spawnBounds.xMin, this.spawnBounds.xMax);
    const z = randRange(this.spawnBounds.zMin, this.spawnBounds.zMax);
    return {
      id: this.nextId++,
      x, z, yaw: 0,
      home: { x, z },
      wanderX: x, wanderZ: z,
      wanderPause: Math.random() * 2,
      mood: 0,
      prevStage: 0,
      respawnAtMs: 0,
      shinyUntilMs: 0,
    };
  }

  // Set per-mode NPC headcount. Race wants ~30, Classic 10, PvE 14.
  // Idempotent — adjusts the live array length without touching state
  // of NPCs that survive the resize.
  setTargetCount(count: number): void {
    this.targetCount = Math.max(0, count);
    while (this.npcs.length > this.targetCount) this.npcs.pop();
    while (this.npcs.length < this.targetCount) this.npcs.push(this.makeNpc());
  }

  // Set per-arena spawn + leave bounds. Must be called BEFORE
  // setTargetCount() at warmup→playing so fresh NPCs spawn in-bounds.
  setSpawnBounds(b: AABB2): void {
    this.spawnBounds = b;
  }

  resetAll() {
    this.npcs = [];
    for (let i = 0; i < this.targetCount; i++) this.npcs.push(this.makeNpc());
  }

  // Debug-only: drop an NPC at the given XZ. Bumps the target count so
  // resetAll() / setTargetCount(prev) won't immediately erase it.
  forceSpawnAt(x: number, z: number, shinyMs = 0): number {
    const npc: ServerNpc = {
      id: this.nextId++,
      x, z, yaw: 0,
      home: { x, z },
      wanderX: x, wanderZ: z,
      wanderPause: Math.random() * 2,
      mood: 0,
      prevStage: 0,
      respawnAtMs: 0,
      shinyUntilMs: shinyMs > 0 ? Date.now() + shinyMs : 0,
    };
    this.npcs.push(npc);
    this.targetCount = Math.max(this.targetCount, this.npcs.length);
    return npc.id;
  }

  getTargetCount(): number {
    return this.targetCount;
  }

  tick(
    dt: number,
    nowMs: number,
    attackers: AttackerInfo[],
    onDamagePlayer: (id: string, dmg: number) => { killed: boolean },
  ): { xp: NpcXpGrant[]; shouts: NpcShoutEvent[] } {
    const xp: NpcXpGrant[] = [];
    const shouts: NpcShoutEvent[] = [];

    for (const npc of this.npcs) {
      // Respawn cooldown: NPC is hidden + inert until the timer elapses,
      // then we roll a brand new one in the same array slot.
      if (npc.respawnAtMs > 0) {
        if (nowMs >= npc.respawnAtMs) {
          const fresh = this.makeNpc();
          const keepId = npc.id;
          Object.assign(npc, fresh);
          npc.id = keepId;
          npc.respawnAtMs = 0;
        } else {
          continue;
        }
      }

      // Find all absorbers overlapping this NPC (both factions can stack).
      const inRange: AttackerInfo[] = [];
      for (const a of attackers) {
        if (NPC_TUNING.HOOD_BLOCKS_ABSORPTION && a.role === 'int' && a.hood) continue;
        const d = Math.hypot(a.x - npc.x, a.z - npc.z);
        const ok = a.role === 'ext'
          ? d <= COMBAT.CHAD_CIRCLE_R
          : d >= COMBAT.VIRGIN_RING_INNER && d <= COMBAT.VIRGIN_RING_OUTER;
        if (ok) inRange.push(a);
      }

      // Apply absorption, dampened by saturation. Each new unit is worth
      // less the more drained the NPC already is — soft diminishing return
      // that replaces the old hard cap. No clamp on mood.
      const sat = saturationFactor(npc.mood);
      let absMagnitude = 0;
      for (const a of inRange) {
        const delta = NPC_TUNING.BASE_ABSORPTION_PER_SEC * a.absorptionMult * sat * dt;
        if (a.role === 'ext') npc.mood -= delta;
        else                  npc.mood += delta;
        absMagnitude += delta;
      }

      // Homeostasis: exponential drift toward neutral. Also unbounded above
      // but in practice the saturation curve keeps mood well under ±300.
      npc.mood *= Math.max(0, 1 - NPC_TUNING.NORMALIZATION_PER_SEC * dt);

      const stage = stageFromMood(npc.mood);

      // XP flows whenever someone is actively absorbing. Skilled players
      // can stretch farm duration by herding the NPC away from the map
      // edge — there's no stage cutoff, only the natural saturation curve
      // reducing each tick's yield as the NPC gets drained.
      if (absMagnitude > 0) {
        const mult = nowMs < npc.shinyUntilMs ? NPC_TUNING.SHINY_MULT : 1;
        const amount = absMagnitude * NPC_TUNING.XP_PER_ABSORPTION_UNIT * mult;
        const share = amount / inRange.length;
        for (const a of inRange) xp.push({ attackerId: a.id, amount: share });
      }

      // Rising-edge shout when NPC first crosses into stage 2 this tick.
      if (stage >= 2 && npc.prevStage < 2) {
        shouts.push({ npcId: npc.id, x: npc.x, z: npc.z, mood: npc.mood });
      }
      npc.prevStage = stage;

      this.moveNpc(npc, dt, stage, inRange);

      // Contact body-check damage at stage 2/3 — reckless leeching hurts,
      // this is the push-back against infinite-farm exploits.
      if (stage >= 2 && inRange.length > 0) {
        const dps = NPC_TUNING.CONTACT_DPS[stage];
        for (const a of inRange) {
          const d = Math.hypot(a.x - npc.x, a.z - npc.z);
          if (d > NPC_TUNING.CONTACT_R) continue;
          const { killed } = onDamagePlayer(a.id, dps * dt);
          if (killed) {
            const shinyActive = this.npcs.reduce((n, x) => n + (x.shinyUntilMs > nowMs ? 1 : 0), 0);
            if (shinyActive < NPC_TUNING.SHINY_CAP) {
              npc.shinyUntilMs = nowMs + NPC_TUNING.SHINY_SEC * 1000;
            }
            // Calm down after a kill — mood resets so the NPC doesn't
            // immediately sprint off.
            npc.mood = 0;
            break;
          }
        }
      }

      // Physical exit check — the only way NPCs actually leave. If a
      // player chased them off the edge while farming, they're gone and
      // will respawn fresh at a new random spawn point.
      const b = this.spawnBounds;
      const m = NPC_TUNING.LEAVE_MARGIN;
      if (npc.x < b.xMin - m || npc.x > b.xMax + m ||
          npc.z < b.zMin - m || npc.z > b.zMax + m) {
        npc.respawnAtMs = nowMs + NPC_TUNING.RESPAWN_DELAY_SEC * 1000;
      }
    }

    return { xp, shouts };
  }

  private moveNpc(npc: ServerNpc, dt: number, stage: 0 | 1 | 2 | 3, inRange: AttackerInfo[]) {
    let targetX: number;
    let targetZ: number;
    let speed: number;

    if (stage >= 1) {
      // Direction of travel driven by mood polarity, not by whoever is
      // closest right now. A fleeing NPC keeps fleeing the faction that
      // drained them even if they briefly leave the weapon shape.
      const wantRole: Role = npc.mood < 0 ? 'ext' : 'int';
      let target: { x: number; z: number } | null = null;
      let bestD = Infinity;
      for (const a of inRange) {
        if (a.role !== wantRole) continue;
        const d = Math.hypot(a.x - npc.x, a.z - npc.z);
        if (d < bestD) { bestD = d; target = { x: a.x, z: a.z }; }
      }
      if (!target) {
        // Nobody of the right faction in range — drift back to wander.
        targetX = npc.wanderX;
        targetZ = npc.wanderZ;
        speed = NPC_TUNING.SPEED[0];
      } else if (npc.mood < 0) {
        const dx = npc.x - target.x;
        const dz = npc.z - target.z;
        const d = Math.hypot(dx, dz) || 1;
        targetX = npc.x + (dx / d) * 10;
        targetZ = npc.z + (dz / d) * 10;
        speed = NPC_TUNING.SPEED[stage];
      } else {
        targetX = target.x;
        targetZ = target.z;
        speed = NPC_TUNING.SPEED[stage];
      }
    } else {
      if (npc.wanderPause > 0) {
        npc.wanderPause -= dt;
        return;
      }
      const dxH = npc.wanderX - npc.x;
      const dzH = npc.wanderZ - npc.z;
      if (Math.hypot(dxH, dzH) < 0.6) {
        const a = Math.random() * Math.PI * 2;
        const r = Math.random() * NPC_TUNING.WANDER_R;
        npc.wanderX = npc.home.x + Math.cos(a) * r;
        npc.wanderZ = npc.home.z + Math.sin(a) * r;
        npc.wanderPause = 0.8 + Math.random() * 1.2;
        return;
      }
      targetX = npc.wanderX;
      targetZ = npc.wanderZ;
      speed = NPC_TUNING.SPEED[0];
    }

    const dx = targetX - npc.x;
    const dz = targetZ - npc.z;
    const d = Math.hypot(dx, dz);
    if (d < 0.05) return;
    const step = speed * dt;
    const k = Math.min(step, d) / d;
    npc.x += dx * k;
    npc.z += dz * k;
    npc.yaw = Math.atan2(dx, dz);
  }

  snapshot(nowMs: number): NpcSnap[] {
    return this.npcs.map((n) => ({
      id: n.id,
      x: n.x,
      z: n.z,
      yaw: n.yaw,
      stage: stageFromMood(n.mood),
      mood: n.mood,
      shiny: nowMs < n.shinyUntilMs,
      visible: n.respawnAtMs === 0,
    }));
  }
}

function randRange(a: number, b: number): number {
  return a + Math.random() * (b - a);
}
